import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../../../components/form/Input';
import Button from '../../../components/form/Button';
import useForm from '../../../hooks/useForm';
import useMutation from '../../../hooks/useMutation';
import { bookAdd, bookEdit } from '../../../services/bookService';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { saveStep } from '../../../services/stepService';
import { setActiveBookId } from '../../../redux/slice/stepSlice';
import { toast } from 'react-toastify';
import { addBook, updateBook } from '../../../redux/slice/profileSlice';
import LoadingFallback from '../../../components/elements/LoadingFallback';
import Checkbox from '../../../components/form/Checkbox';
import { getActiveStep } from '../../../services/commonService';
import { markStepAsVisited } from '../../../redux/slice/profileSlice';
import StepProgressView from '../../../components/common/StepProgressView';

// Validation rules
const validation = {
  bookName: { required: true, message: 'Book Name is required' },
};

const BookCreation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.authProvider);
  const { booksLoading, myBooks } = useSelector((state) => state.profile);
  const { activeBookId } = useSelector((state) => state.stepFlow);

  const stepNumber = getActiveStep();
  const activeBook = myBooks?.find((book) => book._id === activeBookId);

  const { values, handleChange, handleSubmit, errors, formValidate } = useForm({
    validationRules: validation,
    initialState: {
      bookName: activeBook?.title ?? '',
    },
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    if (!!activeBookId) setAgreeToTerms(true);
  }, [activeBookId]);

  // useMutation for bookAdd
  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      if (!!activeBookId) {
        return await bookEdit({
          id: activeBookId,
          title: val.bookName,
        });
      } else {
        return await bookAdd({
          title: val.bookName,
          description: val.bookDescription,
        });
      }
    },
    onSuccess: async (res) => {
      if (![200, 201].includes(res?.status)) {
        console.error('Book creation failed:', res);
        toast.error('Failed to create book');
        return;
      }

      if (!res?.data?._id && !activeBookId) {
        toast.error(
          'Could not save book, something went wrong, please try again.'
        );
        return;
      }

      // Save book ID and add to Redux
      if (!activeBookId) {
        dispatch(setActiveBookId({ bookId: res.data._id }));
        dispatch(addBook(res.data));
      } else {
        dispatch(updateBook({ _id: activeBookId, title: res.data.title }));
      }

      // Save step progress
      try {
        await saveStep({
          userId: session?.user?._id,
          bookId: activeBookId || res.data._id,
          isCompleted: 'yes',
          stepCode: 'Upload_Your_Cover_Photo',
        });
        dispatch(
          markStepAsVisited({
            id: activeBookId,
            currentStepCode: 'Upload_Your_Cover_Photo',
          })
        );
        // markStepAsVisited('Upload_Your_Cover_Photo', activeBookId || res.data._id);

        // Navigate after successful step save
        navigate('/dashboard?stepCode=Upload_Your_Cover_Photo');
      } catch (err) {
        console.error('Failed to save step:', err);
        toast.error('Book created but navigation failed. Please try again.');
      }
    },
    onError: (err) => {
      console.error('Book creation failed:', err);
      toast.error(err?.message || 'Failed to create book');
    },
  });

  if (booksLoading) {
    return <LoadingFallback />;
  }

  return (
    <div className='relative'>
      <h1 className='text-2xl md:text-3xl xl:text-4xl font-bold text-black mb-2 md:mb-4 xl:mb-6 text-center flex gap-5 items-center justify-center flex-col-reverse md:flex-row'>
        {!!activeBookId ? 'Update Your Book Title' : 'Enter Your Book Title'}{' '}
        <div className='h-6 md:h-9 flex items-center justify-center md:px-5 px-3 md:rounded-lg rounded-full border border-md-pink text-md-pink text-[10px] md:text-base'>
          Step {stepNumber} of 5
        </div>
      </h1>
      <StepProgressView number={stepNumber} />
      <div className='bg-white rounded-lg overflow-hidden'>
        <div className='py-6 px-4 md:px-14 lg:px-20'>
          <form
            onSubmit={(event) => handleSubmit(event, mutate)}
            className='space-y-6'
          >
            <div className='relative'>
              <Input
                inputPlaceholder='Enter book title here'
                inputName='bookName'
                inputValue={values?.bookName || activeBook?.title || ''}
                onChange={handleChange}
                {...formValidate(errors, 'bookName')}
                inputClasses={
                  'rounded-lg !bg-white  !border-black placeholder:text-black/50 !text-center text-base md:!text-xl text-black font-bold'
                }
              />
              {!!errors?.bookName?.message && (
                <p className='text-red-500 text-sm text-center mt-2'>
                  <i className='fa-solid fa-circle-exclamation mr-1'></i>
                  {errors?.bookName?.message}
                </p>
              )}
            </div>

            <div className='max-w-lg mx-auto'>
              <Checkbox
                checked={agreeToTerms}
                onChange={() => setAgreeToTerms(!agreeToTerms)}
                checkboxLabel={
                  <>
                    I confirm I have read and agree to the <a className='text-md-pink' href={`${process.env.REACT_APP_SHOPIFY_URL}/policies/terms-of-service`} target='_blank' rel="noreferrer">Terms of Service</a> and <a className='text-md-pink' href={`${process.env.REACT_APP_SHOPIFY_URL}/policies/privacy-policy`} target='_blank' rel="noreferrer">Privacy Policy</a>, and that I have the rights to all images I upload.
                  </>
                }
                checkboxLableClass={'text-slate-500 text-sm'}
                divClass={'!items-start'}
              />
            </div>
            {/* Submit Button */}
            <div className='flex flex-col items-center gap-4 '>
              <Button
                buttonType='submit'
                buttonLabel={!!activeBookId ? 'Next' : 'Create Book'}
                buttonClasses={
                  '!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm'
                }
                buttonLabelClasses={'text-sm !font-bold'}
                isDisable={
                  !agreeToTerms ||
                  isLoading ||
                  !(values?.bookName || activeBook?.title)
                }
                {...(isLoading && {
                  loading: true,
                  buttonIconPosition: 'left',
                })}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className='bg-red-50 border border-red-200 rounded-xl p-4 mt-6'>
                <div className='flex items-start gap-3'>
                  <i className='fa-solid fa-circle-exclamation text-red-500 text-lg mt-0.5'></i>
                  <p className='text-red-700 text-sm'>
                    {error?.message ||
                      'Something went wrong. Please try again.'}
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookCreation;
